"use client";

import { type ComponentPropsWithoutRef, type ComponentRef, forwardRef } from "react";

import { CircleUserRoundIcon, XIcon } from "lucide-react";
import { useMutation, useQueryClient } from "tanstack-react-query-5";

import { Link, useRouter } from "@/shared.i18n/navigation";
import { PrimSheet } from "@/shared-web.ui/prim.sheet";
import { Avatar } from "@/shared-web.ui/ui.avatar";
import { Button } from "@/shared-web.ui/ui.button";
import { IconButton } from "@/shared-web.ui/ui.icon-button";
import { Separator } from "@/shared-web.ui/ui.separator";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";
import { getLoginUrl } from "@/shared-web.ui/util.login";
import { getUserInfoOptions, useGetUserInfo } from "@/stf.data/hook.auth";
import { getCartOptions } from "@/stf.data/hook.cart";
import { useIsLoggedInGuest } from "@/stf.data/hook.use-is-logged-in-guest";
import { GetUserInfoReturn, logout } from "@/stf.data/request.auth";
import { type User } from "@/stf.data/schema";
import { GuestLoginCallout } from "@/stf.ui/ui.guest-login-callout";

type UserButtonRef = ComponentRef<typeof IconButton>;
interface UserButtonProps extends Omit<ComponentPropsWithoutRef<typeof IconButton>, "screenReaders"> {
  userData?: User | null | undefined;
}
const UserButton = forwardRef<
  UserButtonRef,
  UserButtonProps
>((props, ref) => {
  const { userData, ...buttonProps } = props;

  return (
    <IconButton
      {...buttonProps}
      screenReaders="Open user modal"
      className={cn(
        "text-inherit",
        userData?.avatar && "p-[2px]",
      )}
      radius="full"
      variant="ghost"
      ref={ref}
    >
      {userData?.avatar
        ? (
            <Avatar
              size="2"
              src={userData.avatar}
              fallback={
                userData.displayName
                  ? userData.displayName[0]
                  : <CircleUserRoundIcon strokeWidth={1} />
              }
            />
          )
        : <CircleUserRoundIcon strokeWidth={1} />}
    </IconButton>
  );
});
UserButton.displayName = "UserButton";

interface getDisplayNameProps {
  data?: GetUserInfoReturn;
}
const getDisplayName = (props: getDisplayNameProps) => {
  const { data } = props;

  if (/@line\.me$/.test(data?.email || "")) {
    return data?.displayName;
  }
  if (/@guest\.panich$/.test(data?.email || "")) {
    return "Guest";
  }
  return data?.email;
};

const UserNavbar = () => {
  const { data, isFetched } = useGetUserInfo();
  const { isLoggedInGuest } = useIsLoggedInGuest();

  const router = useRouter();

  const queryClient = useQueryClient();

  const { mutate: mutateLogout } = useMutation({
    mutationFn: logout,
    onSettled: async () => {
      await queryClient.invalidateQueries(
        getUserInfoOptions(),
      );
      await queryClient.invalidateQueries(
        getCartOptions(),
      );
    },
  });

  const handleLogout = () => {
    mutateLogout();
    router.push("/");
  };

  if (!isFetched) {
    return null;
  }

  return (
    <div>
      <div className="flex justify-between">
        <Text size="4">
          {getDisplayName({ data })}
        </Text>
        <PrimSheet.Close asChild>
          <IconButton
            color="gray"
            radius="full"
            variant="outline"
            screenReaders="Close sidebar"
          >
            <XIcon />
          </IconButton>
        </PrimSheet.Close>
      </div>
      <Separator className="my-6" size="3" />
      <div className="flex flex-col gap-4">
        <Link href="/my/orders" passHref>
          <PrimSheet.Close asChild className="w-full">
            <Button
              type="button"
              variant="ghost"
              color="gray"
              highContrast
              className="flex justify-start py-4"
            >
              <Text size="3">Orders</Text>
            </Button>
          </PrimSheet.Close>
        </Link>
        <Link href="/my/subscriptions" passHref>
          <PrimSheet.Close asChild className="w-full">

            <Button
              type="button"
              variant="ghost"
              color="gray"
              highContrast
              className="flex justify-start py-4"
            >
              <Text size="3">Subscriptions</Text>
            </Button>
          </PrimSheet.Close>
        </Link>
        {
          isLoggedInGuest
            ? (<GuestLoginCallout />)
            : (
                <PrimSheet.Close asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    color="gray"
                    highContrast
                    className="flex justify-start py-4"
                    onClick={handleLogout}
                  >
                    <Text size="3">Logout</Text>
                  </Button>
                </PrimSheet.Close>
              )
        }
      </div>
    </div>
  );
};

const UserButtonWithNavbar = () => {
  const { data, isFetched } = useGetUserInfo();
  const router = useRouter();

  if (!isFetched) {
    return <div className="m-[-6px] size-9 p-[6px]" />;
  }

  if (!data) {
    return (
      <UserButton
        onClick={() => {
          router.push(getLoginUrl(), { scroll: false });
        }}
        userData={data}
      />
    );
  }

  return (
    <PrimSheet.Root>
      <PrimSheet.Title hidden>My Account</PrimSheet.Title>
      <PrimSheet.Trigger asChild>
        <UserButton
          userData={data}
        />
      </PrimSheet.Trigger>
      <PrimSheet.Content
        side="right"
        className="max-w-80"
      >
        <UserNavbar />
      </PrimSheet.Content>
    </PrimSheet.Root>
  );
};

export {
  UserButton,
  UserButtonWithNavbar,
};
